/**
 * Settings → Tables workflow groups aligned with commercial acf_groups_json domains.
 */
import { catalogSlugForAcfPath } from "./fieldCatalogScan";
import {
  COMMERCIAL_DOMAIN_LIST,
  COMMERCIAL_DOMAIN_SCHEMAS,
  COMMERCIAL_DOMAINS,
} from "./commercialProjectSchema";

export const COMMERCIAL_FIELD_CATALOG_GROUPS = [
  {
    domainKey: COMMERCIAL_DOMAINS.SUMMARY,
    name: "Project summary",
    appliesToCommercial: true,
    appliesToExpansion: true,
  },
  {
    domainKey: COMMERCIAL_DOMAINS.ENGINEERING,
    name: "Engineering",
    appliesToCommercial: true,
    appliesToExpansion: true,
  },
  {
    domainKey: COMMERCIAL_DOMAINS.CONSTRUCTION,
    name: "Construction",
    appliesToCommercial: true,
    appliesToExpansion: true,
  },
  {
    domainKey: COMMERCIAL_DOMAINS.FIBER,
    name: "Fiber",
    appliesToCommercial: true,
    appliesToExpansion: true,
  },
  {
    domainKey: COMMERCIAL_DOMAINS.NETWORKING,
    name: "Networking",
    appliesToCommercial: true,
    appliesToExpansion: true,
  },
  {
    domainKey: COMMERCIAL_DOMAINS.ACCOUNTING,
    name: "Accounting",
    appliesToCommercial: true,
    appliesToExpansion: true,
  },
];

/** Legacy WordPress ACF group titles / keys → commercial domain key. */
export const ACF_GROUP_TO_COMMERCIAL_DOMAIN = {
  [COMMERCIAL_DOMAINS.SUMMARY]: COMMERCIAL_DOMAINS.SUMMARY,
  project_summary_test_2: COMMERCIAL_DOMAINS.SUMMARY,
  "project summary test 2": COMMERCIAL_DOMAINS.SUMMARY,
  [COMMERCIAL_DOMAINS.ENGINEERING]: COMMERCIAL_DOMAINS.ENGINEERING,
  engineering_test_2: COMMERCIAL_DOMAINS.ENGINEERING,
  "engineering test 2": COMMERCIAL_DOMAINS.ENGINEERING,
  [COMMERCIAL_DOMAINS.CONSTRUCTION]: COMMERCIAL_DOMAINS.CONSTRUCTION,
  construction_test_2: COMMERCIAL_DOMAINS.CONSTRUCTION,
  "construction test 2": COMMERCIAL_DOMAINS.CONSTRUCTION,
  [COMMERCIAL_DOMAINS.FIBER]: COMMERCIAL_DOMAINS.FIBER,
  fiber_test_2: COMMERCIAL_DOMAINS.FIBER,
  "fiber test 2": COMMERCIAL_DOMAINS.FIBER,
  splicing_test_2: COMMERCIAL_DOMAINS.FIBER,
  [COMMERCIAL_DOMAINS.NETWORKING]: COMMERCIAL_DOMAINS.NETWORKING,
  networking_test_2: COMMERCIAL_DOMAINS.NETWORKING,
  "networking test 2": COMMERCIAL_DOMAINS.NETWORKING,
  "inventory test 2": COMMERCIAL_DOMAINS.NETWORKING,
  inventory_test_2: COMMERCIAL_DOMAINS.NETWORKING,
  [COMMERCIAL_DOMAINS.ACCOUNTING]: COMMERCIAL_DOMAINS.ACCOUNTING,
  accounting_test_2: COMMERCIAL_DOMAINS.ACCOUNTING,
  "accounting test 2": COMMERCIAL_DOMAINS.ACCOUNTING,
};

/** Grid column slugs → commercial domain (board-level columns). */
export const GRID_COLUMN_TO_COMMERCIAL_DOMAIN = {
  work_order: COMMERCIAL_DOMAINS.SUMMARY,
  work_order_base: COMMERCIAL_DOMAINS.SUMMARY,
  customer: COMMERCIAL_DOMAINS.SUMMARY,
  name: COMMERCIAL_DOMAINS.SUMMARY,
  export_slug: COMMERCIAL_DOMAINS.SUMMARY,
  status: COMMERCIAL_DOMAINS.SUMMARY,
  phase: COMMERCIAL_DOMAINS.SUMMARY,
  active_subphase: COMMERCIAL_DOMAINS.SUMMARY,
  project_type: COMMERCIAL_DOMAINS.SUMMARY,
  subtype: COMMERCIAL_DOMAINS.SUMMARY,
  department: COMMERCIAL_DOMAINS.SUMMARY,
  site_name: COMMERCIAL_DOMAINS.SUMMARY,
  service_location: COMMERCIAL_DOMAINS.SUMMARY,
  a_location: COMMERCIAL_DOMAINS.SUMMARY,
  z_location: COMMERCIAL_DOMAINS.SUMMARY,
  sales_rep: COMMERCIAL_DOMAINS.SUMMARY,
  site_walk: COMMERCIAL_DOMAINS.SUMMARY,
  incident: COMMERCIAL_DOMAINS.SUMMARY,
  numbers_complete: COMMERCIAL_DOMAINS.SUMMARY,
  countersigned: COMMERCIAL_DOMAINS.SUMMARY,
  service_order: COMMERCIAL_DOMAINS.SUMMARY,
  work_order_excel_template: COMMERCIAL_DOMAINS.SUMMARY,
  countersigned_so: COMMERCIAL_DOMAINS.SUMMARY,
  work_order_numbers_field: COMMERCIAL_DOMAINS.SUMMARY,
  project_notes: COMMERCIAL_DOMAINS.SUMMARY,
  preliminary_route: COMMERCIAL_DOMAINS.ENGINEERING,
  construction_footage: COMMERCIAL_DOMAINS.ENGINEERING,
  site_walk_files: COMMERCIAL_DOMAINS.ENGINEERING,
  permit_files: COMMERCIAL_DOMAINS.ENGINEERING,
  drafter: COMMERCIAL_DOMAINS.ENGINEERING,
  splicer: COMMERCIAL_DOMAINS.FIBER,
  assigned_network_tech: COMMERCIAL_DOMAINS.NETWORKING,
  usa_tickets: COMMERCIAL_DOMAINS.CONSTRUCTION,
  asbuilts: COMMERCIAL_DOMAINS.CONSTRUCTION,
  bid_out: COMMERCIAL_DOMAINS.CONSTRUCTION,
  contractor: COMMERCIAL_DOMAINS.CONSTRUCTION,
  construction_contractor: COMMERCIAL_DOMAINS.CONSTRUCTION,
  contract_cost: COMMERCIAL_DOMAINS.CONSTRUCTION,
  contract_execution_date: COMMERCIAL_DOMAINS.CONSTRUCTION,
  contractor_start_date: COMMERCIAL_DOMAINS.CONSTRUCTION,
  contract_expiration_date: COMMERCIAL_DOMAINS.CONSTRUCTION,
  fiber_files: COMMERCIAL_DOMAINS.FIBER,
  contractor_fiber: COMMERCIAL_DOMAINS.FIBER,
  fiber_contractor: COMMERCIAL_DOMAINS.CONSTRUCTION,
  completion_notice: COMMERCIAL_DOMAINS.ACCOUNTING,
  circuit_id: COMMERCIAL_DOMAINS.NETWORKING,
  cut_sheet: COMMERCIAL_DOMAINS.NETWORKING,
  nid_config: COMMERCIAL_DOMAINS.NETWORKING,
  topology: COMMERCIAL_DOMAINS.NETWORKING,
  service_speed: COMMERCIAL_DOMAINS.NETWORKING,
  connection_type: COMMERCIAL_DOMAINS.NETWORKING,
  handoff_type: COMMERCIAL_DOMAINS.NETWORKING,
  number_of_ips: COMMERCIAL_DOMAINS.NETWORKING,
  it_contact: COMMERCIAL_DOMAINS.NETWORKING,
  onsite_contact: COMMERCIAL_DOMAINS.SUMMARY,
  mrc: COMMERCIAL_DOMAINS.ACCOUNTING,
  non_recurring_charge: COMMERCIAL_DOMAINS.ACCOUNTING,
  term_in_months: COMMERCIAL_DOMAINS.ACCOUNTING,
  invoiced_date: COMMERCIAL_DOMAINS.ACCOUNTING,
  accounting_contact: COMMERCIAL_DOMAINS.ACCOUNTING,
};

export function stableCommercialGroupId(domainKey) {
  return `grp_${domainKey}`;
}

export function buildDefaultCommercialFieldCatalogGroups() {
  return COMMERCIAL_FIELD_CATALOG_GROUPS.map((row) => ({
    id: stableCommercialGroupId(row.domainKey),
    name: row.name,
    domainKey: row.domainKey,
    appliesToCommercial: row.appliesToCommercial,
    appliesToExpansion: row.appliesToExpansion,
  }));
}

function catalogFieldTypeFromSchemaType(type) {
  const map = {
    text: "text",
    textarea: "text",
    bool: "boolean",
    select: "selection",
    multiselect: "selection",
    date: "date",
    number: "number",
    currency: "number",
    file: "file",
    multifile: "file",
    readonly: "text",
    repeater: "json",
  };
  return map[String(type || "").trim()] || "text";
}

function appendSchemaFieldRows(domainKey, schemaLabel, groupId, field, pathPrefix, out) {
  if (field.computed) return;
  const path = pathPrefix ? `${pathPrefix}.${field.key}` : field.key;
  if (field.type === "repeater" && Array.isArray(field.fields)) {
    for (const sub of field.fields) {
      if (sub.type === "repeater" && Array.isArray(sub.fields)) {
        for (const nested of sub.fields) {
          appendSchemaFieldRows(domainKey, schemaLabel, groupId, nested, `${path}.${sub.key}`, out);
        }
      } else {
        appendSchemaFieldRows(domainKey, schemaLabel, groupId, sub, path, out);
      }
    }
    return;
  }
  const slug = catalogSlugForAcfPath(domainKey, path);
  if (!slug) return;
  out.push({
    slug,
    label: `${schemaLabel} → ${field.label}`,
    groupId,
    source: "commercial_schema",
    appliesToCommercial: true,
    appliesToExpansion: domainKey === COMMERCIAL_DOMAINS.SUMMARY,
    description: "",
    fieldType: catalogFieldTypeFromSchemaType(field.type),
    acfGroupName: domainKey,
    fieldPath: path,
  });
}

export function buildCommercialSchemaCatalogFields(groups) {
  const groupIdByDomain = new Map(
    groups.filter((g) => g.domainKey).map((g) => [g.domainKey, g.id])
  );
  const out = [];
  for (const domainKey of COMMERCIAL_DOMAIN_LIST) {
    const schema = COMMERCIAL_DOMAIN_SCHEMAS[domainKey];
    if (!schema?.fields) continue;
    const groupId = groupIdByDomain.get(domainKey) || null;
    for (const field of schema.fields) {
      // Cross-domain UI aliases (e.g. Engineering asbuilt → construction) stay on the home domain catalog.
      if (field.valueDomain && field.valueDomain !== domainKey) continue;
      appendSchemaFieldRows(domainKey, schema.label, groupId, field, "", out);
    }
  }
  return out;
}

export function resolveCommercialDomainKey(acfGroupName) {
  const raw = String(acfGroupName || "").trim();
  if (!raw) return null;
  const lower = raw.toLowerCase();
  if (ACF_GROUP_TO_COMMERCIAL_DOMAIN[lower]) return ACF_GROUP_TO_COMMERCIAL_DOMAIN[lower];
  if (COMMERCIAL_DOMAIN_LIST.includes(lower)) return lower;

  if (lower.includes("summary") || lower.includes("project summary")) return COMMERCIAL_DOMAINS.SUMMARY;
  if (lower.includes("engineering") || lower.includes("permit")) return COMMERCIAL_DOMAINS.ENGINEERING;
  if (lower.includes("construction")) return COMMERCIAL_DOMAINS.CONSTRUCTION;
  if (lower.includes("fiber") || lower.includes("splic")) return COMMERCIAL_DOMAINS.FIBER;
  if (lower.includes("network") || lower.includes("inventory")) return COMMERCIAL_DOMAINS.NETWORKING;
  if (lower.includes("account")) return COMMERCIAL_DOMAINS.ACCOUNTING;

  return null;
}

export function resolveCommercialDomainKeyFromSlug(slug) {
  const s = String(slug || "").trim().toLowerCase();
  if (!s) return null;
  for (const domainKey of COMMERCIAL_DOMAIN_LIST) {
    if (s === domainKey || s.startsWith(`${domainKey}__`)) return domainKey;
  }
  const parts = s.split("__");
  if (parts.length >= 2) {
    const fromAcf = resolveCommercialDomainKey(parts[0]);
    if (fromAcf) return fromAcf;
  }
  return GRID_COLUMN_TO_COMMERCIAL_DOMAIN[s] || null;
}

export function resolveFieldCatalogGroupId(groups, acfGroupName, slug = "") {
  const domainKey =
    resolveCommercialDomainKey(acfGroupName) || resolveCommercialDomainKeyFromSlug(slug);
  if (!domainKey) return null;
  const stableId = stableCommercialGroupId(domainKey);
  const byDomain = groups.find((g) => g.domainKey === domainKey);
  if (byDomain) return byDomain.id;
  const byStableId = groups.find((g) => g.id === stableId);
  if (byStableId) return byStableId.id;
  const canonical = COMMERCIAL_FIELD_CATALOG_GROUPS.find((g) => g.domainKey === domainKey);
  if (!canonical) return null;
  const byName = groups.find(
    (g) => String(g.name || "").trim().toLowerCase() === canonical.name.toLowerCase()
  );
  return byName?.id || null;
}

export function inferFieldCatalogGroupId(groups, field) {
  return (
    resolveFieldCatalogGroupId(groups, field?.acfGroupName, field?.slug) ||
    resolveFieldCatalogGroupId(groups, field?.acfGroupName, field?.fieldPath) ||
    null
  );
}

/**
 * Align workflow groups and field assignments with commercial project domains.
 * @param {{ groups: Array, fields: Array }} model
 * @param {{ mkFieldId?: () => string }} [opts]
 */
export function applyCommercialFieldCatalogStructure(model, opts = {}) {
  const mkFieldId =
    opts.mkFieldId ||
    (() => `fld_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`);

  const defaultGroups = buildDefaultCommercialFieldCatalogGroups();
  const groups = [];
  const seenIds = new Set();

  for (const dg of defaultGroups) {
    const existing =
      (model.groups || []).find((g) => g.domainKey === dg.domainKey) ||
      (model.groups || []).find((g) => g.id === dg.id) ||
      (model.groups || []).find(
        (g) => String(g.name || "").trim().toLowerCase() === dg.name.toLowerCase()
      );
    const id = existing?.id || dg.id;
    groups.push({
      id,
      name: dg.name,
      domainKey: dg.domainKey,
      appliesToCommercial: dg.appliesToCommercial,
      appliesToExpansion: dg.appliesToExpansion,
    });
    seenIds.add(id);
  }

  for (const g of model.groups || []) {
    if (!seenIds.has(g.id) && !g.domainKey) {
      groups.push(g);
      seenIds.add(g.id);
    }
  }

  const schemaFields = buildCommercialSchemaCatalogFields(groups);
  const bySlug = new Map((model.fields || []).map((f) => [f.slug, { ...f }]));

  for (const sf of schemaFields) {
    const prev = bySlug.get(sf.slug);
    bySlug.set(sf.slug, {
      id: prev?.id || mkFieldId(),
      slug: sf.slug,
      label: prev?.label && prev.label !== prev.slug ? prev.label : sf.label,
      groupId: sf.groupId,
      source: prev?.source || sf.source,
      appliesToCommercial: prev?.appliesToCommercial ?? sf.appliesToCommercial,
      appliesToExpansion: prev?.appliesToExpansion ?? sf.appliesToExpansion,
      description: prev?.description || sf.description,
      fieldType: prev?.fieldType && prev.fieldType !== "text" ? prev.fieldType : sf.fieldType,
      acfGroupName: prev?.acfGroupName || sf.acfGroupName,
      fieldPath: prev?.fieldPath || sf.fieldPath,
    });
  }

  const fields = [...bySlug.values()].map((f) => {
    const gid = inferFieldCatalogGroupId(groups, f) || f.groupId || null;
    const domainKey =
      resolveCommercialDomainKey(f.acfGroupName) ||
      resolveCommercialDomainKeyFromSlug(f.slug) ||
      GRID_COLUMN_TO_COMMERCIAL_DOMAIN[f.slug];
    const acfGroupName =
      f.acfGroupName ||
      (domainKey && COMMERCIAL_DOMAIN_LIST.includes(domainKey) ? domainKey : "");
    return { ...f, groupId: gid, acfGroupName };
  });

  return { groups, fields };
}
